import { showSupportChat } from 'components/intercom';
import { uniq } from 'lodash';
import PropTypes from 'prop-types';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { ReduxState } from 'types';
import { PERSON_STATUSES, PERSON_STATUSES_NAMES } from 'utils/models/Person';
import { getCustomerField, isCustomerField } from 'utils/util/customerData';
import { useFeatures } from 'utils/util/features';
import { configForLocale } from '../../../locale/messages';
import ConfirmAPI from '../../../utils/api/ConfirmAPI';
import { CAMPAIGN_STATUSES } from '../../../utils/models/Campaign';
import {
  additionalManagerRow,
  additionalManagerRows,
  exportDatasetRecordFields,
  managerFieldValue,
  participantSuggestion,
  validateDatasetRecordManagers,
} from '../../../utils/models/DatasetRecord';
import {
  getDateFormatForDatasetTransform,
  parseDateFromUnicode,
} from '../../../utils/util/time';
import { RowDeleteButton } from '../Inputs/TableEditor';
import { INPUT_TYPES } from '../Inputs/ValidatedInputTypes';
import ModalEditor from './ModalEditor';

// should we treat this as a boolean choice?
// because there can be "Yes", "Y", "NO", "N", etc.
const isBooleanChoice = (options) => {
  if (options.DEFAULT === undefined) {
    return false;
  }

  const possibleValues = uniq(Object.values(options));
  if (
    possibleValues.length != 2 ||
    !possibleValues.includes(true) ||
    !possibleValues.includes(false)
  ) {
    return false;
  }

  return true;
};

// have id, first name, last name, email always come first
const FIELD_NAMES_TO_PRIORITIZE = [
  'external_id',
  'given_name',
  'legal_given_name',
  'preferred_given_name',
  'family_name',
  'full_name',
  'email',
];

const FIELD_NAMES_TO_DEPRIORITIZE = [
  'is_participating',
  'is_participating_in_self_phase',
];

const PARTICIPATION_FIELDS = new Set([
  'is_participating_in_self_phase',
  'is_evaluated_by_manager',
  'is_writing_peer_reviews',
  'is_receiving_peer_reviews',
  'is_participating_in_evaluation_phase',
  'is_writing_upward_manager_review',
  'is_rated',
  'is_only_receiving_review',
  'is_only_selecting_high_priority_peers',
]);

const ModalDatasetRecordEditor: FC<Props> = (props) => {
  const { formatMessage, locale } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [wantToLoad, setWantToLoad] = useState(false);
  const [configs, setConfigs] = useState({});
  const { ifEnabled } = useFeatures();

  // reload the data whenever this is opened/re-opened, so we can
  // guarantee this dialog always has the most up-to-date starting point
  // need this extra variable so that we can allow the dialog to open
  // asynchronously / independently of when the load happens
  useEffect(() => {
    if (props.isOpen != isOpen) {
      if (props.isOpen) {
        setWantToLoad(true);
      } else {
        setWantToLoad(false);
      }
      setIsOpen(props.isOpen);
    }
  }, [isOpen, props.isOpen]);

  // do the actual data loading -- rather than get the data-to-be-changed
  // passed in via props, we just take the person/campaign/org and explicitly
  // call the backend for the latest info
  useEffect(() => {
    if (
      wantToLoad &&
      props.person_id &&
      props.campaign_id &&
      // @ts-expect-error
      props.currentOrganization?.id
    ) {
      ConfirmAPI.sendRequestToConfirm(
        'GET',
        '/survey-response-configs',
        {
          person: props.person_id,
          campaign: props.campaign_id,
          // @ts-expect-error
          organization: props.currentOrganization.id,
        },
        (data, error, message) => {
          if (error) {
            console.error('Could not fetch user configs: ' + message);
            // @ts-expect-error
            setConfigs(null);
            return;
          }
          setConfigs(data);
        }
      );
      setWantToLoad(false);
    }
  }, [
    wantToLoad,
    props.person_id,
    props.campaign_id,
    // @ts-expect-error
    props.currentOrganization.id,
  ]);

  const [mappings, setMappings] = useState();
  useEffect(() => {
    if (
      // @ts-expect-error
      !props.currentOrganization?.id ||
      // @ts-expect-error
      props.currentOrganization?.status === CAMPAIGN_STATUSES.DEMO ||
      !props.campaign_id
    ) {
      return;
    }

    const params = {
      // @ts-expect-error
      organization: props.currentOrganization?.id,
      campaign: props.campaign_id,
    };

    ConfirmAPI.getUrlWithCache(
      '/performance/admin/get-dataset-mappings',
      'dataset_mappings',
      // @ts-expect-error
      props.userSub,
      // @ts-expect-error
      props.currentProxyPerson,
      params,
      (data) => {
        if (data?.keys) {
          setMappings(data);
        }
      },
      () => {
        // @ts-expect-error
        setMappings(null);
      }
    );
  }, [
    // @ts-expect-error
    props.currentOrganization?.status,
    // @ts-expect-error
    props.currentOrganization?.id,
    // @ts-expect-error
    props.currentProxyPerson,
    // @ts-expect-error
    props.userSub,
    props.campaign_id,
  ]);

  // we get the fields to display from props. for now, perf admin gets the fields
  // from campaign mappings but this may change in the future, and this allows the
  // component user to customize what fields to include
  const fields = useMemo(() => {
    // @ts-expect-error
    if (!mappings?.keys) {
      return undefined;
    }
    const fields = {};
    // the ordering of fields is determined by the "order" field in dataset.mappings
    // but defaults to whatever arbitrary order we get from the keys() method
    // @ts-expect-error
    const order = mappings?.order || Object.keys(mappings.keys);
    order.forEach((theirName) => {
      // @ts-expect-error
      const ourName = mappings.keys[theirName];
      const info = {
        name: ourName,
        label: theirName,
      };

      // special handling for dates and multiple-choice
      // TODO: need to handle cost_center / strip_up_to?
      if (
        // @ts-expect-error
        mappings?.transforms &&
        // @ts-expect-error
        ourName in mappings.transforms &&
        ourName.includes('_date')
      ) {
        // @ts-expect-error
        info.type = 'date';
        // extract date format from list of transforms
        // @ts-expect-error
        mappings.transforms[ourName].forEach((transformObject) => {
          const transform = Object.keys(transformObject)[0];
          const format = getDateFormatForDatasetTransform(transform);
          if (format) {
            // @ts-expect-error
            info.transform = transform;
            // @ts-expect-error
            info.format = format.pikr;
            // @ts-expect-error
            info.placeholder = format.placeholder;
            // @ts-expect-error
            info.unicode = format.unicode;
          }
        });
        // @ts-expect-error
      } else if (mappings?.values && ourName in mappings.values) {
        // @ts-expect-error
        const optionMap = mappings.values[ourName];
        if (isBooleanChoice(optionMap)) {
          // @ts-expect-error
          info.type = 'boolean';
          // @ts-expect-error
          info.options = Object.entries(mappings.values[ourName]).map(
            ([label, value]) => ({ name: label, object: label, value })
          );
          // @ts-expect-error
          info.defaultValue = mappings.values[ourName].DEFAULT ?? false;

          // because the modal holds `true` and `false`, we need to map
          // them back to valid "labels" before sending them to the backend.
          // arbitrarily choose labels (that are not "DEFAULT").
          // @ts-expect-error
          info.trueChoice = info.options.find(
            (option) => option.value === true && option.name !== 'DEFAULT'
          ).name;
          // @ts-expect-error
          info.falseChoice = info.options.find(
            (option) => option.value === false && option.name !== 'DEFAULT'
          ).name;
        } else {
          // @ts-expect-error
          info.type = 'choice';
          // @ts-expect-error
          info.options = Object.entries(mappings.values[ourName]).map(
            ([label]) => ({ name: label, object: label })
          );
        }
      } else if (ourName === 'manager_email') {
        // @ts-expect-error
        info.type = 'manager_email_editor';
        // a person is picked, every manager column follows the pick
        info.label = formatMessage({
          id: 'app.views.widgets.modals.modal_dataset_record_editor.manager.label',
          defaultMessage: 'Manager',
        });
      } else if (ourName === 'additional_managers_identifiers') {
        // @ts-expect-error
        info.type = 'table';
        info.label = formatMessage({
          id: 'app.views.widgets.modals.modal_dataset_record_editor.additional_managers.label',
          defaultMessage: 'Additional managers',
        });
        // @ts-expect-error
        info.standaloneRow = true;
        // @ts-expect-error
        info.hide = !ifEnabled('additional_managers', true, false);
      } else if (ourName === 'status') {
        const statusNames = PERSON_STATUSES_NAMES(formatMessage);
        // @ts-expect-error
        info.type = 'choice';
        // @ts-expect-error
        info.options = [PERSON_STATUSES.ACTIVE, PERSON_STATUSES.LEAVE].map(
          (status) => ({ object: status, name: statusNames[status] })
        );
      }

      fields[theirName] = info;
    });
    return fields;
  }, [mappings, formatMessage, ifEnabled]);

  const reverseKeyMapping = useMemo(
    () =>
      Object.fromEntries(
        // @ts-expect-error
        Object.entries(mappings?.keys || {}).map(([k, v]) => [v, k])
      ),
    [mappings]
  );

  const readOnlyFields = useMemo(() => {
    const hasParticipationFields =
      Object.keys(reverseKeyMapping).findIndex((k) =>
        PARTICIPATION_FIELDS.has(k)
      ) >= 0;

    // externally-imported ratings should never be editable in the UI;
    // platform ids are internal identities; the manager's identifiers
    // follow the manager picked
    const fields = [
      'rating',
      'final_rating',
      'platform_id',
      'manager_external_id',
      'manager_platform_id',
    ];

    if (hasParticipationFields) {
      fields.push('is_participating');
    }

    return fields;
  }, [reverseKeyMapping]);

  // render inputs in two groups: regular inputs in rows of 3, then custom data
  // inputs (identified by their id starting with 'ccd-') after a separator
  const renderInputs = useCallback((inputs) => {
    const regularInputs = inputs.filter(
      (input) => !isCustomerField(input?.props?.name)
    );
    const customInputs = inputs.filter((input) =>
      isCustomerField(input?.props?.name)
    );

    const rows = [];
    for (let i = 0; i < regularInputs.length; i += 3) {
      // @ts-expect-error
      rows.push(regularInputs.slice(i, i + 3));
    }
    return (
      <>
        {rows.map((row, index) => (
          <Row key={`row-${index}`} className="align-items-top">
            {/* @ts-expect-error */}
            {row.map((input, iindex) => (
              <Col
                key={`row-${index}-col-${iindex}`}
                className={input?.props?.className ?? 'col-4'}
              >
                {input}
              </Col>
            ))}
          </Row>
        ))}
        {customInputs.length > 0 && (
          <div className="mt-4">
            <hr />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <FormattedMessage
                  id="app.views.widgets.modals.modal_dataset_record_editor.custom_data.title"
                  defaultMessage="Custom data"
                />
              </h6>
            </div>
            <Row>
              {customInputs.map((input, i) => (
                <Col key={i} className="col-4 mb-3">
                  {input}
                </Col>
              ))}
            </Row>
          </div>
        )}
      </>
    );
  }, []);

  const belowFormContents = (
    <div className="mt-4 text-center">
      <FormattedMessage
        id="app.views.widgets.modals.modal_dataset_record_editor.questions_or_concerns"
        defaultMessage="These changes will only affect this cycle. Questions or concerns?"
      />{' '}
      <button
        className="btn btn-link ps-0"
        onClick={() => {
          showSupportChat();
        }}
      >
        <FormattedMessage
          id="app.views.widgets.modals.modal_dataset_record_editor.let_us_know"
          defaultMessage="Let us know."
        />
      </button>
    </div>
  );

  const inputs = useMemo(() => {
    if (!fields) {
      return [];
    }

    const unorderedInputs = Object.entries(fields)
      // @ts-expect-error
      .filter(([_name, info]) => !info.hide)
      .map(([name, info]) => {
        // @ts-expect-error
        const ourName = mappings?.keys[name] || name;
        const input = {
          required: false,
          name: name,
          // @ts-expect-error
          label: info.label,
          labelClassName: 'small',
          autoFocus: true,
          maxLength: 100,
          hideMaxLengthIndicator: true,
          // don't allow editing of the below fields
          // as we take them from the person object
          disabled:
            FIELD_NAMES_TO_PRIORITIZE.indexOf(ourName) !== -1 ||
            readOnlyFields.indexOf(ourName) !== -1 ||
            // @ts-expect-error
            info?.disabled,
          // @ts-expect-error
          standaloneRow: !!info.standaloneRow,
          // @ts-expect-error
          className: info?.className,
        };
        // @ts-expect-error
        if (info.type === 'date') {
          // @ts-expect-error
          input.type = INPUT_TYPES.DATE_PICKER;
          input.autoFocus = false;
          // @ts-expect-error
          input.transform = info.transform;
          // @ts-expect-error
          input.dateFormat = info.format;
          // @ts-expect-error
          input.altInput = true;
          // @ts-expect-error
          input.altFormat = configForLocale(locale).dateFormat;
          // @ts-expect-error
          input.placeholder = configForLocale(locale).datePlaceholder;
          // @ts-expect-error
        } else if (info.type === 'choice') {
          // @ts-expect-error
          input.type = INPUT_TYPES.SELECT;
          input.autoFocus = false;
          // @ts-expect-error
          input.clearable = true;
          // @ts-expect-error
          input.defaultOptions = info.options;
          // @ts-expect-error
          input.isOptionSelected = (option, selectValue) => {
            return option.name === selectValue.name;
          };
          // @ts-expect-error
        } else if (info.type === 'boolean') {
          // @ts-expect-error
          input.type = INPUT_TYPES.SWITCH;
          // @ts-expect-error
          input.defaultValue = info.defaultValue;
          input.autoFocus = false;
          // @ts-expect-error
        } else if (info.type === 'table') {
          // @ts-expect-error
          input.type = INPUT_TYPES.TABLE_EDITOR;
          // an unsaved form row goes at once, with a bin icon
          // @ts-expect-error
          input.moreActions = RowDeleteButton;
          // @ts-expect-error
          input.confirmDelete = false;
          // each additional manager is picked among the participants; the
          // identifier columns follow the pick and are not typed by hand
          // @ts-expect-error
          input.columns = [
            {
              name: (
                <FormattedMessage
                  id="app.views.widgets.modals.modal_dataset_record_editor.additional_managers_identifiers.manager.name"
                  defaultMessage="Additional manager"
                />
              ),
              field: 'name',
              sortable: false,
              type: INPUT_TYPES.AUTOSUGGEST,
              minSearchLength: 2,
              debounceMs: 250,
              getSuggestionValue: (x) => x.object.name || x.object.email,
              elasticsearchOptions: {
                url: 'get-survey-responses-by-name',
                getQuery: (query) => ({
                  name: query,
                  campaign_id: props.campaign_id,
                }),
                mapFunction: participantSuggestion,
              },
              rowValueOnChange: additionalManagerRow,
            },
            {
              name: (
                <FormattedMessage
                  id="app.views.widgets.modals.modal_dataset_record_editor.additional_managers_identifiers.email.name"
                  defaultMessage="Email"
                />
              ),
              field: 'email',
              sortable: false,
              editable: false,
            },
            ...(reverseKeyMapping?.external_id
              ? [
                  {
                    name: (
                      <FormattedMessage
                        id="app.views.widgets.modals.modal_dataset_record_editor.additional_managers_identifiers.external_id.name"
                        defaultMessage="External ID"
                      />
                    ),
                    field: 'external_id',
                    sortable: false,
                    editable: false,
                  },
                ]
              : []),
            ...(reverseKeyMapping?.platform_id
              ? [
                  {
                    name: (
                      <FormattedMessage
                        id="app.views.widgets.modals.modal_dataset_record_editor.additional_managers_identifiers.platform_id.name"
                        defaultMessage="Platform ID"
                      />
                    ),
                    field: 'platform_id',
                    sortable: false,
                    editable: false,
                  },
                ]
              : []),
          ];
          input.autoFocus = false;
          // @ts-expect-error
        } else if (info.type === 'manager_email_editor') {
          // @ts-expect-error
          input.type = INPUT_TYPES.AUTOSUGGEST;
          input.autoFocus = false;
          // @ts-expect-error
          input.minSearchLength = 2;
          // @ts-expect-error
          input.debounceMs = 250;
          // @ts-expect-error
          input.getSuggestionValue = (x) => {
            return x.object.name || x.object.email;
          };
          // @ts-expect-error
          input.elasticsearchOptions = {
            url: 'get-survey-responses-by-name',
            getQuery: (query) => {
              return { name: query, campaign_id: props.campaign_id };
            },
            mapFunction: participantSuggestion,
          };
        }
        return input;
      });

    // put FIELD_NAMES_TO_PRIORITIZE first in UI
    const inputsAsObject = unorderedInputs.reduce((acc, input) => {
      // @ts-expect-error
      acc[mappings?.keys[input.name]] = input;
      return acc;
    }, {});

    const prioritizedInputs = FIELD_NAMES_TO_PRIORITIZE.map(
      (i) => inputsAsObject[i]
    ).filter((x) => !!x);
    const otherInputs = unorderedInputs.filter(
      (i) =>
        // @ts-expect-error
        FIELD_NAMES_TO_PRIORITIZE.indexOf(mappings?.keys[i.name]) === -1 &&
        // @ts-expect-error
        FIELD_NAMES_TO_DEPRIORITIZE.indexOf(mappings?.keys[i.name]) === -1 &&
        !i.standaloneRow
    );

    const deprioritizedInputs = FIELD_NAMES_TO_DEPRIORITIZE.map(
      (i) => inputsAsObject[i]
    ).filter((x) => !!x);

    const standaloneRowInputs = unorderedInputs
      .filter((i) => i.standaloneRow)
      // @ts-expect-error
      .reduce((acc, input) => [...acc, { ...input, className: 'col-12' }], []);

    const inputCount =
      prioritizedInputs.length +
      otherInputs.length +
      deprioritizedInputs.length;

    const trailingPlaceholdersCount = (3 - (inputCount % 3)) % 3;

    return [
      ...prioritizedInputs,
      ...otherInputs,
      ...deprioritizedInputs,
      ...Array.from({ length: trailingPlaceholdersCount }, (_, i) => i).map(
        (i) => ({
          type: INPUT_TYPES.CUSTOM_INPUT,
          component: (
            <div key={'placeholder_' + i} className="col-4">
              {''}
            </div>
          ),
        })
      ),
      // @ts-expect-error
      ...standaloneRowInputs,
      ...(props.customerDataColumns ?? []).map((col) => ({
        type: INPUT_TYPES.TEXT,
        label: col.label,
        name: getCustomerField(col.key),
      })),
    ];
  }, [
    fields,
    readOnlyFields,
    // @ts-expect-error
    mappings?.keys,
    props.campaign_id,
    reverseKeyMapping?.external_id,
    reverseKeyMapping?.platform_id,
    locale,
    props.customerDataColumns,
  ]);

  // translate from external (e.g. survey response config) data to useful format for this modal
  const importData = useCallback(
    (data, fields) => {
      // @ts-expect-error
      const modalData = { id: configs.id };
      Object.entries(fields).forEach(([name, info]) => {
        if (name in data) {
          // @ts-expect-error
          if (info.type === 'date') {
            modalData[name] = data[name]
              ? // @ts-expect-error
                parseDateFromUnicode(data[name], info.unicode)
              : null;
            // @ts-expect-error
          } else if (info.type === 'choice') {
            modalData[name] =
              data[name] !== undefined && data[name] !== null
                ? // @ts-expect-error
                  info.options.find((x) => x.object === data[name])
                : undefined;
            // @ts-expect-error
          } else if (info.type === 'boolean') {
            modalData[name] =
              data[name] !== undefined && data[name] !== null
                ? // @ts-expect-error
                  info.options.find((x) => x.object === data[name]).value
                : undefined;
            // @ts-expect-error
          } else if (info.type === 'manager_email_editor') {
            modalData[name] = managerFieldValue(data, reverseKeyMapping);
            // @ts-expect-error
          } else if (info.name === 'additional_managers_identifiers') {
            modalData[name] = additionalManagerRows(
              data[name],
              data._additional_managers_names
            );
          } else {
            modalData[name] = data[name];
          }
        } else {
          modalData[name] = undefined;
        }
      });
      return modalData;
    },
    // @ts-expect-error
    [configs.id, reverseKeyMapping]
  );

  const exportData = useCallback(
    (modalData) => {
      const data = {
        // @ts-expect-error
        id: configs.id,
        ...exportDatasetRecordFields(
          modalData,
          fields ?? {},
          reverseKeyMapping
        ),
      };

      // include custom campaign data if there are visible columns
      if (props.customerDataColumns?.length) {
        const customerData = props.customerDataColumns.reduce((acc, curr) => {
          acc[curr.key] = modalData[getCustomerField(curr.key)];
          return acc;
        }, {});
        data['_customer_data'] = customerData;
      }

      return data;
    },
    [
      reverseKeyMapping,
      fields,
      // @ts-expect-error
      configs.id,
      props.customerDataColumns,
    ]
  );

  const dataObject = useMemo(() => {
    if (!fields || !configs) {
      return;
    }
    const obj = importData(configs, fields);

    // @ts-expect-error
    const customerData = configs?._customer_data ?? {};
    const columns = props.customerDataColumns ?? [];
    columns.forEach((col) => {
      obj[getCustomerField(col.key)] = customerData[col.key] ?? '';
    });
    return obj;
  }, [configs, fields, importData, props.customerDataColumns]);

  const onValidate = useCallback(
    (modalData) =>
      validateDatasetRecordManagers(
        modalData,
        fields ?? {},
        dataObject ?? {},
        formatMessage({
          id: 'app.views.widgets.modals.modal_dataset_record_editor.validation.pick_manager',
          defaultMessage: 'Select a person from the list.',
        })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields, dataObject, formatMessage]
  );

  return (
    <ModalEditor
      size="lg"
      method="PATCH"
      url="/survey-response-configs"
      title={`${formatMessage({
        id: 'app.views.widgets.modals.modal_dataset_record_editor.title.edit',
        defaultMessage: 'Edit',
      })} ${
        configs[reverseKeyMapping.preferred_given_name] ||
        configs[reverseKeyMapping.legal_given_name] ||
        configs[reverseKeyMapping.given_name]
      } ${configs[reverseKeyMapping.family_name]} (${props.campaign_name})`}
      submitText={formatMessage({
        id: 'app.views.widgets.modals.modal_dataset_record_editor.submit_text.save',
        defaultMessage: 'Save',
      })}
      renderInputs={renderInputs}
      // @ts-expect-error
      isHorizontalLayout={true}
      object={dataObject}
      inputs={inputs}
      isOpen={props.isOpen}
      toggle={props.toggle}
      onClosed={props.onClosed}
      transformObjectBeforeSubmit={exportData}
      onValidate={onValidate}
      callback={props.onSaved}
      belowFormContents={belowFormContents}
    />
  );
};

const ModalDatasetRecordEditor_propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onClosed: PropTypes.func,
  onSaved: PropTypes.func.isRequired,
  currentOrganization: PropTypes.object.isRequired,
  campaign_name: PropTypes.string,
  campaign_id: PropTypes.number,
  person_id: PropTypes.number,
  customerDataColumns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired
  ),
};

type Props = PropTypes.InferProps<typeof ModalDatasetRecordEditor_propTypes>;

const mapStateToProps = (state: ReduxState) => {
  const { currentOrganization, currentProxyPerson } = state;

  return {
    currentOrganization,
    currentProxyPerson,
  };
};

export default connect(mapStateToProps)(React.memo(ModalDatasetRecordEditor));
